import type { ReactNode } from "react";
import type { ActionFunction } from "@remix-run/node";
import { Form, useActionData } from "@remix-run/react";
import { ForgotPasswordForm } from "~/components/forms/forgot-password-form";
import { requestPasswordReset } from "~/services/password-reset.server";

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get("email") as string;

  if (!email) {
    return Response.json(
      { error: "Sähköpostiosoite on pakollinen" },
      { status: 400 }
    );
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return Response.json(
      { error: "Virheellinen sähköpostiosoite" },
      { status: 400 }
    );
  }

  try {
    // Use Rails/Rodauth reset password request endpoint for employees
    const response = await requestPasswordReset({ email });

    // 200 OK and 202 Accepted are both valid success responses for password reset
    if (response.ok && (response.status === 200 || response.status === 202)) {
      return Response.json({
        success: true,
        message:
          "Jos sähköpostiosoite on rekisteröity, saat pian salasanan palautuslinkin sähköpostiisi.",
      });
    }

    if (response.status === 404) {
      // Don't reveal if email exists or not for security
      return Response.json({
        success: true,
        message:
          "Jos sähköpostiosoite on rekisteröity, saat pian salasanan palautuslinkin.",
      });
    }

    throw new Error(`Failed to send reset email. Status: ${response.status}`);
  } catch (error) {
    return Response.json(
      {
        error: "Tapahtui virhe. Yritä uudelleen myöhemmin.",
      },
      { status: 500 }
    );
  }
};

export default function ForgotPasswordPage(): ReactNode {
  const actionData = useActionData<{
    error?: string;
    success?: boolean;
    message?: string;
  }>();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Form method="post">
        <ForgotPasswordForm
          error={actionData?.error}
          success={actionData?.success}
          message={actionData?.message}
          userType="coach"
        />
      </Form>
    </div>
  );
}
